import { useState, useMemo, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { AddCandidatesModal } from '../components/candidates'

// Types for dashboard data
interface Role {
  id: string
  title: string
  levelRange: string
  locations: string[]
  candidatesCount: number
  readinessStatus: 'ready' | 'pending' | 'conflict'
  readinessLabel: string
  signalSummary: string
  lastEvaluationAt?: string
  decisionOwner?: string
}

interface DecisionSummary {
  ready: number
  pending: number
  atRisk: number
}

interface NextAction {
  id: string
  type: 'invite' | 'review' | 'finalize'
  title: string
  description: string
  roleTitle?: string
}

// Mock data for demonstration - in production this comes from API
const MOCK_ROLES: Role[] = [
  {
    id: '1',
    title: 'Backend Engineer',
    levelRange: 'Senior–Principal',
    locations: ['San Francisco', 'Remote'],
    candidatesCount: 5,
    readinessStatus: 'ready',
    readinessLabel: 'Decision ready — high confidence',
    signalSummary: '4 evaluators aligned · Strong systems design · Meets bar at Senior level',
    lastEvaluationAt: '2 hours ago',
    decisionOwner: 'Sarah K.',
  },
  {
    id: '2',
    title: 'Product Designer',
    levelRange: 'Mid–Senior',
    locations: ['New York', 'Remote'],
    candidatesCount: 8,
    readinessStatus: 'pending',
    readinessLabel: 'Needs one more evaluator',
    signalSummary: 'Strong portfolio signal · Needs independent product judgment',
    lastEvaluationAt: '1 day ago',
  },
  {
    id: '3',
    title: 'Engineering Manager',
    levelRange: 'Senior',
    locations: ['San Francisco'],
    candidatesCount: 3,
    readinessStatus: 'conflict',
    readinessLabel: 'Signal conflict — review recommended',
    signalSummary: 'Leadership concerns conflict with technical evaluations · Level calibration mismatch',
    lastEvaluationAt: '4 hours ago',
    decisionOwner: 'Mike T.',
  },
]

const MOCK_ACTIONS: NextAction[] = [
  {
    id: '1',
    type: 'finalize',
    title: 'Finalize decision for Backend Engineer',
    description: 'All required signals collected',
    roleTitle: 'Backend Engineer',
  },
  {
    id: '2',
    type: 'review',
    title: 'Review conflicting evaluations',
    description: 'AI flagged misalignment on problem-solving criteria',
    roleTitle: 'Engineering Manager',
  },
  {
    id: '3',
    type: 'invite',
    title: 'Invite an evaluator',
    description: 'Increase confidence by adding an independent reviewer',
    roleTitle: 'Product Designer',
  },
]

// Icons
function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ExclamationTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  )
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
    </svg>
  )
}

function DocumentMagnifyingGlassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m5.231 13.481L15 17.25m-4.5-15H5.625c-.621 0-1.125.504-1.125 1.125v16.5c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9zm3.75 11.625a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  )
}

function CheckBadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
    </svg>
  )
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  )
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  )
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  )
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
    </svg>
  )
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}

function BriefcaseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" />
    </svg>
  )
}

function DocumentPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m3.75 9v6m3-3H9m1.5-12H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
    </svg>
  )
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function ArrowRightOnRectangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
    </svg>
  )
}

function UserCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function Cog6ToothIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

// Navigation icons
function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
    </svg>
  )
}

function RectangleStackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6.878V6a2.25 2.25 0 012.25-2.25h7.5A2.25 2.25 0 0118 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 004.5 9v.878m13.5-3A2.25 2.25 0 0119.5 9v.878m0 0a2.246 2.246 0 00-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0121 12v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6c0-.98.626-1.813 1.5-2.122" />
    </svg>
  )
}

function UserGroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  )
}

function IdentificationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
    </svg>
  )
}

function Bars3Icon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
  )
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  )
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  )
}

// Navigation configuration
interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  badge?: string // Only for blockers like "Needs review"
}

const NAVIGATION: NavItem[] = [
  { name: 'Decisions', href: '/dashboard', icon: ScaleIcon },
  { name: 'Roles', href: '/roles', icon: RectangleStackIcon },
  { name: 'Candidates', href: '/candidates', icon: IdentificationIcon },
  { name: 'Evaluators', href: '/evaluators', icon: UserGroupIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
]

// Left navigation component
function LeftNav({ 
  currentPath, 
  needsReviewCount = 0,
  mobileOpen,
  onMobileClose,
  collapsed,
  onToggleCollapse
}: { 
  currentPath: string
  needsReviewCount?: number
  mobileOpen: boolean
  onMobileClose: () => void
  collapsed: boolean
  onToggleCollapse: () => void
}) {
  // Add badge only if there are decisions needing review
  const navWithBadges = NAVIGATION.map(item => ({
    ...item,
    badge: item.name === 'Decisions' && needsReviewCount > 0 
      ? `${needsReviewCount} need${needsReviewCount === 1 ? 's' : ''} review`
      : undefined
  }))

  // Mobile nav content (always expanded)
  const mobileNavContent = (
    <nav className="flex-1 px-3 py-4 space-y-1">
      {navWithBadges.map((item) => {
        const isActive = currentPath === item.href || 
          (item.href !== '/dashboard' && currentPath.startsWith(item.href))
        const Icon = item.icon
        
        return (
          <Link
            key={item.name}
            to={item.href}
            onClick={onMobileClose}
            className={`
              group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${isActive 
                ? 'bg-brand-50 text-brand-700' 
                : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'
              }
            `}
          >
            <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-brand-600' : 'text-surface-400 group-hover:text-surface-600'}`} />
            <span className="flex-1">{item.name}</span>
            {item.badge && (
              <span className="text-xs text-surface-500 bg-surface-100 px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </Link>
        )
      })}
    </nav>
  )

  // Desktop nav content (supports collapsed state)
  const desktopNavContent = (
    <nav className="flex-1 px-2 py-4 space-y-1">
      {navWithBadges.map((item) => {
        const isActive = currentPath === item.href || 
          (item.href !== '/dashboard' && currentPath.startsWith(item.href))
        const Icon = item.icon
        
        return (
          <Link
            key={item.name}
            to={item.href}
            title={collapsed ? item.name : undefined}
            className={`
              group flex items-center rounded-lg text-sm font-medium transition-all relative
              ${collapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'}
              ${isActive 
                ? 'bg-brand-50 text-brand-700' 
                : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'
              }
            `}
          >
            <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-brand-600' : 'text-surface-400 group-hover:text-surface-600'}`} />
            {!collapsed && (
              <>
                <span className="flex-1">{item.name}</span>
                {item.badge && (
                  <span className="text-xs text-surface-500 bg-surface-100 px-2 py-0.5 rounded-full">
                    {item.badge}
                  </span>
                )}
              </>
            )}
            {/* Collapsed state: show dot indicator for badge */}
            {collapsed && item.badge && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-amber-400 rounded-full" />
            )}
          </Link>
        )
      })}
    </nav>
  )

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-surface-900/20 backdrop-blur-sm lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile drawer */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-surface-200 transform transition-transform duration-200 ease-in-out lg:hidden
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-surface-200">
          <Link to="/" className="flex items-center gap-2" onClick={onMobileClose}>
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8" />
            <span className="font-semibold text-lg text-brand-600">JumpstoneHR</span>
          </Link>
          <button
            onClick={onMobileClose}
            className="p-2 rounded-lg text-surface-500 hover:bg-surface-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        {mobileNavContent}
      </div>

      {/* Desktop sidebar */}
      <div className={`
        hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:border-r lg:border-surface-200 lg:bg-white
        transition-all duration-200 ease-in-out
        ${collapsed ? 'lg:w-16' : 'lg:w-56'}
      `}>
        {/* Header */}
        <div className={`flex items-center h-16 border-b border-surface-200 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
          <Link to="/" className="flex items-center gap-2" title={collapsed ? 'JumpstoneHR' : undefined}>
            <img src="/images/logo.png" alt="JumpstoneHR" className="w-8 h-8" />
            {!collapsed && <span className="font-semibold text-lg text-brand-600">JumpstoneHR</span>}
          </Link>
        </div>
        
        {desktopNavContent}
        
        {/* Collapse toggle button */}
        <div className={`border-t border-surface-200 p-2 ${collapsed ? 'flex justify-center' : ''}`}>
          <button
            onClick={onToggleCollapse}
            className={`
              flex items-center gap-2 rounded-lg text-sm text-surface-500 hover:bg-surface-100 hover:text-surface-700 transition-colors
              ${collapsed ? 'p-2 justify-center' : 'px-3 py-2 w-full'}
            `}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <ChevronRightIcon className="w-5 h-5" />
            ) : (
              <>
                <ChevronLeftIcon className="w-5 h-5" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </div>
    </>
  )
}

// First-time user empty state component
function EmptyState() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="max-w-2xl mx-auto text-center space-y-10">
        {/* Headline */}
        <div className="space-y-4">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-surface-900 tracking-tight">
            Make your first hiring decision clearer.
          </h1>
          <p className="text-lg text-surface-600 max-w-lg mx-auto">
            Get started in three simple steps. No need to change your existing ATS.
          </p>
        </div>

        {/* Three-step setup */}
        <div className="grid sm:grid-cols-3 gap-6 text-left">
          <div className="relative p-6 rounded-2xl bg-gradient-to-br from-brand-50 to-white border border-brand-100">
            <div className="absolute -top-3 -left-1 w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center text-sm font-semibold shadow-lg shadow-brand-500/30">
              1
            </div>
            <div className="pt-2">
              <div className="w-10 h-10 rounded-xl bg-brand-100 flex items-center justify-center mb-4">
                <BriefcaseIcon className="w-5 h-5 text-brand-600" />
              </div>
              <h3 className="font-semibold text-surface-900 mb-1">Define a role</h3>
              <p className="text-sm text-surface-600">Set the title, level range, and what great looks like.</p>
            </div>
          </div>

          <div className="relative p-6 rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100">
            <div className="absolute -top-3 -left-1 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center text-sm font-semibold shadow-lg shadow-emerald-500/30">
              2
            </div>
            <div className="pt-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                <DocumentPlusIcon className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-surface-900 mb-1">Add candidates</h3>
              <p className="text-sm text-surface-600">Upload resumes, paste LinkedIn, or add referrals.</p>
            </div>
          </div>

          <div className="relative p-6 rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-100">
            <div className="absolute -top-3 -left-1 w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm font-semibold shadow-lg shadow-amber-500/30">
              3
            </div>
            <div className="pt-2">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center mb-4">
                <UsersIcon className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="font-semibold text-surface-900 mb-1">Get signal</h3>
              <p className="text-sm text-surface-600">Invite evaluators or let AI extract initial insights.</p>
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <div className="space-y-4">
          <Link
            to="/roles/new"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-brand-600 text-white font-semibold text-lg hover:bg-brand-700 transition-all shadow-lg shadow-brand-500/25 hover:shadow-brand-500/40 hover:-translate-y-0.5"
          >
            Create your first role
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
          <p className="text-sm text-surface-500">
            Jumpstone works alongside your existing tools. No migration required.
          </p>
        </div>
      </div>
    </div>
  )
}

// Role card component
function RoleCard({ role }: { role: Role }) {
  const statusConfig = {
    ready: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-700',
      icon: CheckCircleIcon,
      iconColor: 'text-emerald-500',
    },
    pending: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-700',
      icon: ClockIcon,
      iconColor: 'text-amber-500',
    },
    conflict: {
      bg: 'bg-surface-100',
      border: 'border-surface-300',
      text: 'text-surface-700',
      icon: ExclamationTriangleIcon,
      iconColor: 'text-surface-500',
    },
  }

  const config = statusConfig[role.readinessStatus]
  const StatusIcon = config.icon

  return (
    <Link
      to={`/roles/${role.id}/decision`}
      className="block group"
    >
      <div className="p-6 rounded-2xl bg-white border border-surface-200 hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/5 transition-all duration-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-surface-900 group-hover:text-brand-700 transition-colors">
              {role.title}
            </h3>
            <p className="text-sm text-surface-500">{role.levelRange}</p>
          </div>
          <div className="flex items-center gap-1.5 text-surface-500">
            <UsersIcon className="w-4 h-4" />
            <span className="text-sm font-medium">{role.candidatesCount}</span>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1.5 text-sm text-surface-600 mb-4">
          <MapPinIcon className="w-4 h-4 text-surface-400" />
          <span>{role.locations.join(' · ')}</span>
        </div>

        {/* Status badge */}
        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.bg} ${config.border} border`}>
          <StatusIcon className={`w-4 h-4 ${config.iconColor}`} />
          <span className={`text-sm font-medium ${config.text}`}>{role.readinessLabel}</span>
        </div>

        {/* Signal summary - explains why the decision is in this state */}
        <p className="mt-3 text-sm text-surface-500 leading-relaxed">
          {role.signalSummary}
        </p>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-surface-100 flex items-center justify-between text-sm text-surface-500">
          {role.lastEvaluationAt && (
            <span>Last evaluation: {role.lastEvaluationAt}</span>
          )}
          {role.decisionOwner && (
            <span className="flex items-center gap-1">
              <span className="w-5 h-5 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-medium">
                {role.decisionOwner.charAt(0)}
              </span>
              {role.decisionOwner}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

// Next action component
function NextActionItem({ action }: { action: NextAction }) {
  const actionConfig = {
    invite: {
      icon: UserPlusIcon,
      bg: 'bg-brand-50',
      iconColor: 'text-brand-600',
    },
    review: {
      icon: DocumentMagnifyingGlassIcon,
      bg: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    finalize: {
      icon: CheckBadgeIcon,
      bg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
  }

  const config = actionConfig[action.type]
  const ActionIcon = config.icon

  return (
    <button className="w-full flex items-center gap-4 p-4 rounded-xl bg-white border border-surface-200 hover:border-brand-300 hover:shadow-md transition-all duration-200 text-left group">
      <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center shrink-0`}>
        <ActionIcon className={`w-5 h-5 ${config.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-surface-900 group-hover:text-brand-700 transition-colors">
          {action.title}
        </h4>
        <p className="text-sm text-surface-500 truncate">{action.description}</p>
      </div>
      <ArrowRightIcon className="w-5 h-5 text-surface-400 group-hover:text-brand-600 group-hover:translate-x-1 transition-all" />
    </button>
  )
}

// Main dashboard component
export default function DashboardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, signOut } = useAuth()
  
  // Mobile nav state
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  
  // Sidebar collapsed state (persisted in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed')
    return saved === 'true'
  })
  
  const handleToggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const newValue = !prev
      localStorage.setItem('sidebar-collapsed', String(newValue))
      return newValue
    })
  }
  
  // User menu dropdown state
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  
  // Add candidates modal state
  const [showAddCandidatesModal, setShowAddCandidatesModal] = useState(false)
  
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // In production, this would come from an API call
  const [roles] = useState<Role[]>(MOCK_ROLES)
  const [actions] = useState<NextAction[]>(MOCK_ACTIONS)
  
  // Check if this is a first-time user (no roles)
  const isFirstTimeUser = roles.length === 0
  
  // Calculate decision summary
  const decisionSummary = useMemo<DecisionSummary>(() => {
    return roles.reduce(
      (acc, role) => {
        if (role.readinessStatus === 'ready') acc.ready++
        else if (role.readinessStatus === 'pending') acc.pending++
        else if (role.readinessStatus === 'conflict') acc.atRisk++
        return acc
      },
      { ready: 0, pending: 0, atRisk: 0 }
    )
  }, [roles])

  const totalCandidates = useMemo(() => {
    return roles.reduce((sum, role) => sum + role.candidatesCount, 0)
  }, [roles])

  const handleSignOut = () => {
    setUserMenuOpen(false)
    signOut()
    navigate('/')
  }

  // Get greeting based on time of day
  const greeting = useMemo(() => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }, [])

  // Determine primary CTA based on state
  const primaryCTA = useMemo(() => {
    if (roles.length === 0) {
      return { label: 'Create your first role', href: '/roles/new', isModal: false }
    }
    return { label: 'Add candidates', href: '#', isModal: true }
  }, [roles])

  // Handle primary CTA click
  const handlePrimaryCTAClick = (e: React.MouseEvent) => {
    if (primaryCTA.isModal) {
      e.preventDefault()
      setShowAddCandidatesModal(true)
    }
  }

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Left Navigation */}
      <LeftNav 
        currentPath={location.pathname}
        needsReviewCount={decisionSummary.atRisk}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />

      {/* Main content area - offset for desktop sidebar */}
      <div className={`transition-all duration-200 ease-in-out ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-56'}`}>
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-surface-200">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-lg text-surface-500 hover:bg-surface-100"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>

              {/* Page title (desktop) */}
              <div className="hidden lg:block">
                <h1 className="text-lg font-semibold text-surface-900">Decisions</h1>
              </div>

              {/* Right side */}
              <div className="flex items-center gap-3">
                <Link
                  to={primaryCTA.href}
                  onClick={handlePrimaryCTAClick}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-600 text-white font-medium text-sm hover:bg-brand-700 transition-colors shadow-sm"
                >
                  <PlusIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">{primaryCTA.label}</span>
                </Link>
                
                {/* User menu */}
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-surface-600 hover:bg-surface-100 transition-colors"
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-medium text-sm">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <ChevronDownIcon className={`hidden sm:block w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dropdown menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-surface-200 focus:outline-none z-50">
                      <div className="p-3 border-b border-surface-100">
                        <p className="text-sm font-medium text-surface-900 truncate">
                          {user?.fullName || 'User'}
                        </p>
                        <p className="text-sm text-surface-500 truncate">
                          {user?.email}
                        </p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/settings/profile"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                        >
                          <UserCircleIcon className="w-5 h-5 text-surface-400" />
                          Your profile
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                        >
                          <Cog6ToothIcon className="w-5 h-5 text-surface-400" />
                          Settings
                        </Link>
                      </div>
                      <div className="border-t border-surface-100 py-1">
                        <button
                          onClick={handleSignOut}
                          className="flex w-full items-center gap-3 px-3 py-2 text-sm text-surface-700 hover:bg-surface-50 transition-colors"
                        >
                          <ArrowRightOnRectangleIcon className="w-5 h-5 text-surface-400" />
                          Sign out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8">
        {isFirstTimeUser ? (
          <EmptyState />
        ) : (
          <div className="space-y-8">
            {/* Welcome section */}
            <div>
              <h1 className="text-2xl font-display font-semibold text-surface-900">
                {greeting}
              </h1>
              <p className="text-surface-600 mt-1">
                Here's where your hiring decisions stand.
              </p>
            </div>

            {/* Decision Readiness Overview - Hero Section */}
            <div className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-brand-900 via-brand-800 to-brand-950 text-white relative overflow-hidden">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="dashboard-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#dashboard-grid)" />
                </svg>
              </div>
              
              <div className="relative z-10">
                <div className="mb-6">
                  <h2 className="text-lg font-medium text-brand-200 mb-1">Hiring decisions in progress</h2>
                  <p className="text-3xl sm:text-4xl font-display font-semibold">
                    {roles.length} role{roles.length !== 1 ? 's' : ''} active · {totalCandidates} candidate{totalCandidates !== 1 ? 's' : ''} under evaluation
                  </p>
                </div>

                {/* Readiness indicators */}
                <div className="grid sm:grid-cols-3 gap-4">
                  {decisionSummary.ready > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                      <CheckCircleIcon className="w-6 h-6 text-emerald-400" />
                      <div>
                        <div className="text-xl font-semibold">{decisionSummary.ready} decision{decisionSummary.ready !== 1 ? 's' : ''} ready</div>
                        <div className="text-sm text-brand-200">High confidence · evaluator alignment achieved</div>
                      </div>
                    </div>
                  )}

                  {decisionSummary.pending > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                      <ClockIcon className="w-6 h-6 text-amber-400" />
                      <div>
                        <div className="text-xl font-semibold">{decisionSummary.pending} decision{decisionSummary.pending !== 1 ? 's' : ''} pending</div>
                        <div className="text-sm text-brand-200">Missing structured feedback or evaluations</div>
                      </div>
                    </div>
                  )}

                  {decisionSummary.atRisk > 0 && (
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/10 backdrop-blur-sm">
                      <ExclamationTriangleIcon className="w-6 h-6 text-surface-300" />
                      <div>
                        <div className="text-xl font-semibold">{decisionSummary.atRisk} decision{decisionSummary.atRisk !== 1 ? 's' : ''} need review</div>
                        <div className="text-sm text-brand-200">Inconsistent standards or conflicting signals</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Two column layout for roles and actions */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Active Roles Section */}
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-surface-900">Active Roles</h2>
                  <Link
                    to="/roles/new"
                    className="text-sm font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1"
                  >
                    <PlusIcon className="w-4 h-4" />
                    New role
                  </Link>
                </div>

                <div className="grid gap-4">
                  {roles.map((role) => (
                    <RoleCard key={role.id} role={role} />
                  ))}
                </div>
              </div>

              {/* Next Actions Section */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-surface-900">Next Actions</h2>
                <div className="space-y-3">
                  {actions.slice(0, 3).map((action) => (
                    <NextActionItem key={action.id} action={action} />
                  ))}
                </div>
              </div>
            </div>

            {/* Trust / Fairness Footer */}
            <div className="pt-8 border-t border-surface-200">
              <div className="flex items-center justify-center gap-2 text-sm text-surface-500">
                <ShieldCheckIcon className="w-4 h-4" />
                <span>Every evaluation is structured, attributable, and auditable. Designed to support fair, consistent hiring decisions.</span>
              </div>
            </div>
          </div>
        )}
        </main>
      </div>

      {/* Add Candidates Modal */}
      <AddCandidatesModal
        isOpen={showAddCandidatesModal}
        onClose={() => setShowAddCandidatesModal(false)}
        roles={roles}
      />
    </div>
  )
}

